import { useCallback, useEffect, useRef, useState } from "react";
import { useAudioRecording } from "./use-audio-recording";

export const useRecordDockState = (
  onSend?: (audioBlob: Blob, files?: File[], transcript?: string) => void,
  onAutoSendComplete?: () => void,
) => {
  const {
    recordingState,
    startRecording,
    stopRecording,
    pauseRecording,
    resumeRecording,
    playRecordedAudio,
    pausePlayback,
    resumePlayback,
    getRecordedAudioBlob,
    updateRecordingState,
    maxRecordingDuration,
    maxDurationReached,
    resetMaxDurationReached,
  } = useAudioRecording();
  const [recordingComplete, setRecordingComplete] = useState(false);
  const [isListening, setIsListening] = useState(false);

  useEffect(() => {
    // Start recording when component mounts
    startRecording();
  }, []);

  useEffect(() => {
    // Cleanup when component unmounts
    return () => {
      if (recordingState.isRecording) {
        stopRecording();
      }
    };
  }, [recordingState.isRecording]);

  const handleStopAndSend = useCallback((files?: File[]) => {
    stopRecording();
    const audioBlob = getRecordedAudioBlob();
    if (audioBlob && onSend) {
      onSend(audioBlob, files);
    }
  }, [onSend]);

  // Stable ref for use in max-duration effect
  const stopAndSendRef = useRef(handleStopAndSend);
  stopAndSendRef.current = handleStopAndSend;

  const onAutoSendCompleteRef = useRef(onAutoSendComplete);
  onAutoSendCompleteRef.current = onAutoSendComplete;

  // Auto-send when max duration is reached
  useEffect(() => {
    if (maxDurationReached && !recordingComplete) {
      resetMaxDurationReached();
      stopAndSendRef.current();
      onAutoSendCompleteRef.current?.();
    }
  }, [maxDurationReached, recordingComplete]);

  // Reset listening state when audio finishes playing completely
  useEffect(() => {
    if (
      isListening && !recordingState.isPlayingRecording &&
      recordingState.currentPlayTime === 0 && recordingState.audioDuration > 0
    ) {
      // Small delay to ensure this isn't triggered by temporary state changes
      const timeout = setTimeout(() => {
        setIsListening(false);
      }, 100);
      return () => clearTimeout(timeout);
    }
    return undefined;
  }, [
    isListening,
    recordingState.isPlayingRecording,
    recordingState.currentPlayTime,
    recordingState.audioDuration,
  ]);

  const handleStopRecording = () => {
    stopRecording();
    setRecordingComplete(true);
  };

  const handleSendRecording = (files?: File[]) => {
    const audioBlob = getRecordedAudioBlob();
    if (audioBlob && onSend) {
      onSend(audioBlob, files);
    }
  };

  const handleListenRecording = () => {
    playRecordedAudio();
    setIsListening(true);
  };

  const handlePausePlayback = () => {
    pausePlayback();
  };

  const handleResumePlayback = () => {
    resumePlayback();
  };

  const handleReRecord = () => {
    // Stop any current playback before revoking URL
    pausePlayback();

    // Reset recording state to clear previous recording (revoke blob URL to prevent memory leak)
    if (recordingState.recordedAudioURL) {
      try { URL.revokeObjectURL(recordingState.recordedAudioURL); } catch {}
    }
    updateRecordingState({
      recordedAudioURL: null,
      isPlayingRecording: false,
      audioDuration: 0,
      currentPlayTime: 0,
    });

    // Reset UI state
    setRecordingComplete(false);
    setIsListening(false);

    // Start new recording
    startRecording();
  };

  const cancelRecording = () => {
    // Stop recording if active
    stopRecording();

    // Reset recording state
    if (recordingState.recordedAudioURL) {
      try { URL.revokeObjectURL(recordingState.recordedAudioURL); } catch {}
    }
    updateRecordingState({
      recordedAudioURL: null,
      isPlayingRecording: false,
      audioDuration: 0,
      currentPlayTime: 0,
      isRecording: false,
      isPaused: false,
      recordingTime: 0,
    });

    // Reset UI state
    setRecordingComplete(false);
    setIsListening(false);
  };

  const getPlaybackProgress = () => {
    if (recordingState.audioDuration > 0) {
      const progress =
        (recordingState.currentPlayTime / recordingState.audioDuration) * 100;
      // Cap at 100% and handle precision issues
      // Also add a small buffer to prevent showing 100% too early
      const cappedProgress = Math.min(Math.max(progress, 0), 99.9);
      return cappedProgress;
    }
    return 0;
  };

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, "0")}:${
      secs.toString().padStart(2, "0")
    }`;
  };

  const getTimerColor = (): string => {
    if (maxRecordingDuration <= 0) return "#b7bdcb";
    const percent = recordingState.recordingTime / maxRecordingDuration;
    if (percent >= 0.9) return "#d65f46"; // red at 90%
    if (percent >= 0.7) return "#F9A825"; // yellow at 70%
    return "#b7bdcb"; // default muted
  };

  return {
    recordingState,
    recordingComplete,
    isListening,
    pauseRecording,
    resumeRecording,
    handleStopRecording,
    handleStopAndSend,
    handleSendRecording,
    handleListenRecording,
    handlePausePlayback,
    handleResumePlayback,
    handleReRecord,
    cancelRecording,
    getPlaybackProgress,
    formatTime,
    maxRecordingDuration,
    getTimerColor,
    setRecordingComplete,
    setIsListening,
  };
};
